import React, { useState, useEffect } from "react";
import { Box, MenuItem, Menu, useMediaQuery, IconButton } from "@mui/material";
import styles from "../../assets/css/modules/navbar.module.css";
import { Link, NavLink } from "react-router-dom";
import { useDispatch } from "react-redux";
import { handleLogout } from "../../redux/reducers/authReducer";
import styled from "styled-components";
import Searchbar from "./Searchbar";
import MobileLayout from "./MobileLayout";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { useQuery } from "react-query";
import axiosInstance from "../../helpers/AxiosInstance";
import Popover from "@mui/material/Popover";
import { ButtonsContainer, Notification } from "../utilities";
import LogoutModal from "../modals/LogoutModal";
import Translation from "../utilities/Translation";
import { useLocation } from "react-router-dom";
import logoImg from "../../assets/images/logo.png";

const NavbarBtn = styled(Link)`
  color: white;
  background: var(--gradient);
  font-size: 13px;
  font-weight: 600;
  display: flex;
  align-items: center;
  justify-content: center;
  text-decoration: none;
  border-radius: 8px;
  padding: 0 18px;
  height: 38px;
  white-space: nowrap;
  transition: opacity 0.2s ease, transform 0.15s ease;
  &:hover {
    opacity: 0.9;
    transform: translateY(-1px);
  }
`;

const Card = styled.div`
  display: flex;
  flex-direction: column;
  width: 520px;
  min-width: 300px;
  height: 480px;
  background: #ffffff 0% 0% no-repeat padding-box;
  box-shadow: 0px 3px 6px #00000029;
  opacity: 1;
  @media (max-width: 1440px) {
    width: 400px;
  }
  @media (max-width: 425px) {
    min-width: 300px;
  }
`;

const Line = styled.hr`
  border: 1px solid var(--primary-color);
`;

const Button = styled(Link)`
  min-width: 162px;
  height: 50px;
  background: transparent;
  display: grid;
  place-items: center;
  text-align: center;
  font-weight: 700;
  font-family: Cairo;
  font-size: 18px;
  letter-spacing: 0px;
  color: #010e3f;
  text-decoration: none;
  opacity: 1;
  @media (max-width: 1440px) {
    font-size: 18px;
  }
`;

export default function Navbar({ sideOpen, RoleID }) {
  const { pathname } = useLocation();
  const { t } = useTranslation("layout");
  const matches = useMediaQuery((theme) => theme.breakpoints.down("md"));
  const dispatch = useDispatch();
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);
  const [logoutModalOpen, setLogoutModalOpen] = useState(false);
  const [anchor, setAnchor] = useState(null);
  const openNotification = Boolean(anchor);
  const handleNotificationClick = (event) => {
    setAnchor(event.currentTarget);
  };
  const [notifications, setNotifications] = useState([]);
  const instance = axiosInstance();
  const { data } = useQuery(
    "notifications",
    async () => {
      const { data } = await instance.get("/GetActiveNotifications");
      return data;
    },
    {
      staleTime: 60000,
      cacheTime: 0,
      refetchOnWindowFocus: false,
    }
  );

  useEffect(() => {
    if (data) {
      setNotifications(data.notifications);
    }
  }, [data]);

  const { CompanyName } = useSelector((state) => ({
    CompanyName: state.Auth.companyData.CompanyName,
  }));
  const { ClientName } = useSelector((state) => ({
    ClientName: state.Auth.AuthedUser.ClientName,
  }));
  const handleClose = () => {
    setAnchorEl(null);
  };
  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };
  const handleLogoutClick = () => {
    setAnchorEl(null);
    setLogoutModalOpen(true);
  };

  if (matches) {
    return <MobileLayout RoleID={RoleID} />;
  }

  return (
    <div className={styles.navbar}>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          width: "100%",
        }}
      >
        {/* Left side - Logo & Company */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: "14px",
          }}
        >
          {/* Company Logo */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: "10px",
            }}
          >
            <Box
              sx={{
                width: "auto",
                minWidth: "120px",
                height: "44px",
                background: "#ffffff",
                borderRadius: "10px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
                padding: "4px 12px",
                "@media (max-width: 965px)": {
                  display: "none",
                },
              }}
            >
              <Box
                component="img"
                src={logoImg}
                sx={{
                  width: "100%",
                  height: "100%",
                  objectFit: "contain",
                  maxHeight: "36px"
                }}
                alt="Logo"
              />
            </Box>
          </Box>
        </Box>

        {/* Right side - Actions */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: "10px",
          }}
        >
          {RoleID === 2 && (
            <>
              {/* Translation */}
              <Translation isMobile={true} />

              {/* Notification Bell */}
              <IconButton
                onClick={handleNotificationClick}
                sx={{
                  position: "relative",
                  width: 38,
                  height: 38,
                  borderRadius: "10px",
                  backgroundColor: "transparent",
                  "&:hover": {
                    backgroundColor: "rgba(255,255,255,0.08)",
                  },
                  "&::before": {
                    content: "''",
                    position: "absolute",
                    top: "4px",
                    right: "4px",
                    width: "10px",
                    borderRadius: "50%",
                    height: "10px",
                    backgroundColor: data?.notifications?.length
                      ? "#ef4444"
                      : "transparent",
                    border: data?.notifications?.length
                      ? "2px solid white"
                      : "none",
                  },
                }}
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="#ffffff"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
                  <path d="M13.73 21a2 2 0 0 1-3.46 0" />
                </svg>
              </IconButton>

              {/* User Avatar */}
              <Box
                onClick={handleClick}
                sx={{
                  width: 38,
                  height: 38,
                  borderRadius: "10px",
                  background: "rgba(255, 255, 255, 0.15)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  cursor: "pointer",
                  transition: "transform 0.15s ease",
                  "&:hover": {
                    transform: "scale(1.05)",
                  },
                }}
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="18"
                  height="18"
                  viewBox="0 0 24 24"
                  fill="white"
                >
                  <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z" />
                </svg>
              </Box>
            </>
          )}

          {RoleID === 1 && (
            <>
              <Translation isMobile={true} />
              <Box
                onClick={handleClick}
                sx={{
                  width: 38,
                  height: 38,
                  borderRadius: "10px",
                  background: "rgba(255, 255, 255, 0.15)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  cursor: "pointer",
                }}
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="18"
                  height="18"
                  viewBox="0 0 24 24"
                  fill="white"
                >
                  <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z" />
                </svg>
              </Box>
            </>
          )}
        </Box>
      </Box>

      {/* User Menu Dropdown */}
      <Menu
        anchorEl={anchorEl}
        id="account-menu"
        open={open}
        onClose={handleClose}
        onClick={handleClose}
        PaperProps={{
          elevation: 0,
          sx: {
            overflow: "visible",
            filter: "drop-shadow(0px 4px 16px rgba(0,0,0,0.12))",
            mt: 1.5,
            borderRadius: "10px",
            minWidth: "160px",
            "& .MuiAvatar-root": {
              width: 32,
              height: 32,
              mr: 1,
            },
          },
        }}
        transformOrigin={{ horizontal: "right", vertical: "top" }}
        anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
      >
        <Box sx={{ padding: "8px 16px 4px", borderBottom: "1px solid #f0f0f0", mb: 1 }}>
          <Box sx={{ fontWeight: 600, fontSize: "14px", color: "#1a1a2e" }}>
            {ClientName}
          </Box>
        </Box>
        <MenuItem
          component={NavLink}
          to="/change-password"
          sx={{
            fontSize: "13px",
            padding: "8px 16px",
            color: "#4b5563",
            "&:hover": {
              backgroundColor: "#f9fafb",
            },
          }}
        >
          {t("Navbar.change_password")}
        </MenuItem>
        <MenuItem
          onClick={handleLogoutClick}
          sx={{
            fontSize: "13px",
            padding: "8px 16px",
            color: "#ef4444",
            "&:hover": {
              backgroundColor: "#fef2f2",
            },
          }}
        >
          {t("Navbar.signout")}
        </MenuItem>
      </Menu>

      {/* ============================================================= */}
      <Popover
        open={openNotification}
        anchorEl={anchor}
        onClose={() => setAnchor(null)}
        onClick={() => {
          setAnchor(null);
        }}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "left",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
        sx={{ fontFamily: "Cairo" }}
      >
        <Card>
          <div dir="rtl">
            {notifications.slice(0, 3).map((notification, index) => (
              <Notification
                key={index}
                setNotifications={setNotifications}
                notifications={notifications}
                notificationIndex={index}
                AWB={notification.AWB}
                Body={notification.Body}
                Status={notification.StatusName}
                Serial={notification.Serial}
                ConsigneeName={notification.ConsigneeName}
                ConsigneePhone={notification.ConsigneePhone}
                RunnerName={notification.RunnerName}
                RunnerPhone={notification.RunnerPhone}
                ReasonName={notification.ReasonName}
                COD={notification.COD}
              />
            ))}
            <Line />
            {notifications.length ? (
              <ButtonsContainer
                style={{ marginTop: "10px", marginBottom: "10px" }}
              >
                <Button to="/notifications" onClick={() => setAnchor(null)}>
                  {t("Navbar.all_notifications")}
                </Button>
              </ButtonsContainer>
            ) : (
              <ButtonsContainer
                style={{
                  marginTop: "10px",
                  marginBottom: "10px",
                  fontSize: "18px",
                }}
              >
                {t("Navbar.no_Notification")}
              </ButtonsContainer>
            )}
          </div>
        </Card>
      </Popover>

      {/* Logout Confirmation Modal */}
      <LogoutModal
        isOpen={logoutModalOpen}
        onClose={() => setLogoutModalOpen(false)}
      />
    </div>
  );
}
